// client/src/api/proxy.js

// Optional: point at a specific edge origin in dev/prod (e.g. http://127.0.0.1:8787)
const EDGE_ORIGIN = import.meta.env.VITE_EDGE_ORIGIN || "https://api.yebo-ai.com";
const DEBUG_PROXY_HEADERS = /^(1|true|yes|on)$/i.test(String(import.meta.env.VITE_DEBUG_PROXY_HEADERS || ""));
const DEBUG_PROXY_VERBOSE = /^(1|true|yes|on)$/i.test(String(import.meta.env.VITE_DEBUG_PROXY_VERBOSE || import.meta.env.VITE_DEBUG_API || ""));


import { getAuthToken } from "./auth.js";

// Optional: if your Worker requires a bearer for /api/* locally
let DEV_PROXY_BEARER = (import.meta.env.VITE_PROXY_BEARER || "").trim();
export function setProxyBearer(token) {
  DEV_PROXY_BEARER = String(token || "").trim();
}

// Widget token — set after fetching /config, sent as X-Widget-Token on widget requests
let widgetToken = "";
export function setWidgetToken(token) {
  widgetToken = String(token || "").trim();
}
export function getWidgetToken() {
  return widgetToken;
}

// Staff API key — for tables management routes (/api/tables/*)
const STAFF_API_KEY = (import.meta.env.VITE_STAFF_API_KEY || "").trim();

// Normalize params (snake_case -> camelCase) and inject default locationId
function ensureLocationId(params = {}) {
  const next = { ...params };

  // normalize legacy snake_case
  if (next.location_id && !next.locationId) {
    next.locationId = String(next.location_id);
    delete next.location_id;
  }

  // inject default from env when not explicitly provided
  const loc = import.meta.env.VITE_LOCATION_ID;
  if (loc && !next.locationId) {
    next.locationId = String(loc);
  }
  return next;
}

function buildQuery(params = {}) {
  const p = ensureLocationId(params);
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) {
    if (v !== undefined && v !== null && v !== "") usp.append(k, String(v));
  }
  return usp.toString(); // NOTE: no leading "?"
}

function withTimeout(promise, ms = 15000, controller) {
  if (!ms) return promise;
  const id = setTimeout(() => controller.abort("timeout"), ms);
  return promise.finally(() => clearTimeout(id));
}

function redactHeaders(headers = {}) {
  const out = { ...(headers || {}) };
  for (const key of Object.keys(out)) {
    if (/authorization|api[-_]?key|token|secret|cookie/i.test(key)) {
      out[key] = "[redacted]";
    }
  }
  return out;
}

function debugProxy(event, payload) {
  if (!DEBUG_PROXY_VERBOSE && !DEBUG_PROXY_HEADERS) return;
  console.info(`[proxy] ${event}`, payload);
}

async function http(path, { method = "GET", headers = {}, params, body, timeoutMs = 15000 } = {}) {
  // Build base URL
  let url = `${EDGE_ORIGIN}/api${path}`;

  // Safely append query (handles existing ? in path)
  const q = buildQuery(params || {});
  if (q) url += (url.includes("?") ? "&" : "?") + q;

  const requestMeta = {
    method,
    path,
    url,
    params: ensureLocationId(params || {}),
    timeoutMs,
  };

  const controller = new AbortController();
  const hasAuthHeader =
    Object.prototype.hasOwnProperty.call(headers, "Authorization") ||
    Object.prototype.hasOwnProperty.call(headers, "authorization");

  // Route-aware auth headers
  const isTablesRoute = path.startsWith("/tables/");
  const isWidgetRoute = !isTablesRoute && path.startsWith("/widget/");
  const authHeaders = {};

  if (isTablesRoute && STAFF_API_KEY && !hasAuthHeader) {
    // Staff routes: inject Bearer from env
    authHeaders.Authorization = `Bearer ${STAFF_API_KEY}`;
  } else if (!hasAuthHeader) {
    // Use session token (preferred) or static bearer (fallback)
    const token = getAuthToken() || DEV_PROXY_BEARER;
    if (token) authHeaders.Authorization = `Bearer ${token}`;
  }

  if (isWidgetRoute && widgetToken) {
    authHeaders["X-Widget-Token"] = widgetToken;
  }

  const init = {
    method,
    signal: controller.signal,
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...authHeaders,
      ...(headers || {}),
    },
  };
  if (method !== "GET" && method !== "HEAD" && body !== undefined) {
    init.body = typeof body === "string" ? body : JSON.stringify(body);
  }

  if (DEBUG_PROXY_HEADERS) {
    debugProxy("request", {
      ...requestMeta,
      headers: redactHeaders(init.headers),
      hasInjectedAuth: Boolean(authHeaders.Authorization),
      hasWidgetToken: Boolean(authHeaders["X-Widget-Token"]),
      body: body === undefined ? undefined : body,
    });
  } else if (DEBUG_PROXY_VERBOSE) {
    debugProxy("request", {
      ...requestMeta,
      hasBody: body !== undefined,
    });
  }

  let res;
  try {
    res = await withTimeout(fetch(url, init), timeoutMs, controller);
  } catch (e) {
    debugProxy("network_error", {
      ...requestMeta,
      error: String(e?.message || e),
    });
    const err = new Error(`Proxy fetch failed: ${e?.message || e}`);
    err.cause = e;
    throw err;
  }

  const requestId = res.headers.get("x-request-id") || "";

  debugProxy("response", {
    ...requestMeta,
    ok: res.ok,
    status: res.status,
    requestId,
    contentType: res.headers.get("content-type") || "",
    upstreamVersion: res.headers.get("x-upstream-version") || "",
    secretSource: res.headers.get("x-secret-source") || "",
    debugPath: res.headers.get("x-debug-path") || "",
    debugOriginalPath: res.headers.get("x-debug-original-path") || "",
  });

  if (!res.ok) {
    let text = "";
    try { text = await res.text(); } catch {}
    let message = text;
    try {
      const j = JSON.parse(text);
      message = j.message || j.error || text;
    } catch {}
    const err = new Error(`HTTP ${res.status} ${method} ${url} — ${message}`);
    err.status = res.status;
    err.requestId = requestId;
    err.body = text;
    debugProxy("error", {
      ...requestMeta,
      status: res.status,
      requestId,
      responseBody: text,
    });
    throw err;
  }

  if (res.status === 204) return null;

  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) return res.json();

  const text = await res.text();
  try { return JSON.parse(text); } catch { return text; }
}

export const proxyGet    = (p, o = {}) => http(p, { ...o, method: "GET" });
export const proxyPost   = (p, body, o = {}) => http(p, { ...o, method: "POST", body });
export const proxyPut    = (p, body, o = {}) => http(p, { ...o, method: "PUT", body });
export const proxyPatch  = (p, body, o = {}) => http(p, { ...o, method: "PATCH", body });
export const proxyDelete = (p, o = {}) => http(p, { ...o, method: "DELETE" });
